import { Controller } from "@hotwired/stimulus"

// 옵션 그룹/선택지 편집을 클라이언트에서 pending 상태로 관리하고
// "완료" 클릭 시 batch_save로 일괄 저장, "닫기" 클릭 시 폐기 confirm.
export default class extends Controller {
  static values = { brandId: Number }

  groupCreates    = []
  groupDestroys   = new Set()
  choiceCreates   = []
  choiceDestroys  = []
  // groupDefaultsChanged: groupId -> Set<choiceId> (변경된 그룹만 추적)
  groupDefaultsChanged = new Map()
  tempCounter     = 0
  orderChanged    = false

  get isDirty() {
    return this.groupCreates.length > 0 ||
           this.groupDestroys.size > 0 ||
           this.choiceCreates.length > 0 ||
           this.choiceDestroys.length > 0 ||
           this.groupDefaultsChanged.size > 0 ||
           this.orderChanged
  }

  // ── 그룹 추가 ──────────────────────────────────────────────

  addGroup(event) {
    event.preventDefault()
    const form = event.target
    const name = form.querySelector('[name="name"]').value.trim()
    const type = form.querySelector('[name="selection_type"]').value
    if (!name) return

    const tempId = `temp-group-${++this.tempCounter}`
    this.groupCreates.push({ tempId, name, selection_type: type })
    document.getElementById("option_group_list")
            ?.insertAdjacentHTML("beforeend", this.buildGroupRow(tempId, name, type))
    form.reset()
  }

  // ── 그룹 삭제 ──────────────────────────────────────────────

  deleteGroup(event) {
    event.preventDefault()
    const { groupId, groupName } = event.currentTarget.dataset
    if (!confirm(`"${groupName}" 그룹을 삭제하시겠습니까?`)) return

    if (groupId.startsWith("temp-group-")) {
      this.groupCreates = this.groupCreates.filter(g => g.tempId !== groupId)
    } else {
      this.groupDestroys.add(groupId)
      this.choiceCreates = this.choiceCreates.filter(c => c.group_id !== groupId)
    }
    this.groupDefaultsChanged.delete(groupId)
    document.getElementById(`group-row-${groupId}`)?.remove()
  }

  // ── 선택지 추가 ────────────────────────────────────────────

  addChoice(event) {
    event.preventDefault()
    const form       = event.target
    const groupId    = form.dataset.groupId
    const name       = form.querySelector('[name="name"]').value.trim()
    const priceDelta = parseInt(form.querySelector('[name="price_delta"]').value.replace(/,/g, '')) || 0
    if (!name) return

    const tempId = `temp-choice-${++this.tempCounter}`
    this.choiceCreates.push({ tempId, group_id: groupId, name, price_delta: priceDelta, is_default: false })

    const tagsEl = document.getElementById(`choice-tags-${groupId}`)
    if (tagsEl) {
      tagsEl.querySelector("[data-no-choices]")?.remove()
      tagsEl.insertAdjacentHTML("beforeend", this.buildChoiceTag(groupId, tempId, name, priceDelta, false))
    }
    form.reset()
  }

  // ── 선택지 삭제 ────────────────────────────────────────────

  deleteChoice(event) {
    event.preventDefault()
    event.stopPropagation()
    const { groupId, choiceId } = event.currentTarget.dataset

    if (choiceId.startsWith("temp-choice-")) {
      this.choiceCreates = this.choiceCreates.filter(c => c.tempId !== choiceId)
    } else {
      this.choiceDestroys.push({ group_id: groupId, choice_id: choiceId })
    }

    if (this.groupDefaultsChanged.has(groupId)) {
      this.groupDefaultsChanged.get(groupId).delete(choiceId)
    }

    const tagEl  = document.getElementById(`choice-tag-${groupId}-${choiceId}`)
    const tagsEl = document.getElementById(`choice-tags-${groupId}`)
    tagEl?.remove()

    if (tagsEl && !tagsEl.querySelector('[id^="choice-tag-"]')) {
      tagsEl.insertAdjacentHTML("beforeend",
        '<span data-no-choices class="text-default-slate/40">선택지 없음</span>')
    }
  }

  // ── 기본값 설정 ────────────────────────────────────────────

  setDefaultChoice(event) {
    event.preventDefault()
    const { groupId, choiceId } = event.currentTarget.dataset
    const groupRow = document.getElementById(`group-row-${groupId}`)
    const tagsEl   = document.getElementById(`choice-tags-${groupId}`)
    if (!tagsEl || !groupRow) return

    const isMulti  = groupRow.dataset.selectionType === "multi"
    const tagEl    = document.getElementById(`choice-tag-${groupId}-${choiceId}`)
    const isActive = tagEl?.dataset.isDefault === "true"

    if (!this.groupDefaultsChanged.has(groupId)) {
      // 첫 변경 시 현재 DOM 상태를 초기값으로 복사 (data-is-default 기준)
      const currentDefaults = new Set(
        [...tagsEl.querySelectorAll('[id^="choice-tag-"]')]
          .filter(el => el.dataset.isDefault === "true")
          .map(el => el.id.replace(`choice-tag-${groupId}-`, ""))
      )
      this.groupDefaultsChanged.set(groupId, currentDefaults)
    }

    const defaults = this.groupDefaultsChanged.get(groupId)

    if (isMulti) {
      // 다중 선택: 마지막 선택지는 해제 불가 (최소 1개 유지)
      if (isActive) {
        if (defaults.size <= 1) return
        defaults.delete(choiceId)
        this.#deactivateTag(tagEl)
      } else {
        defaults.add(choiceId)
        this.#activateTag(tagEl)
      }
    } else {
      // 단일 선택: 이미 선택된 항목 재클릭 시 무시 (최소 1개 유지)
      if (isActive) return

      tagsEl.querySelectorAll('[id^="choice-tag-"]').forEach(el => this.#deactivateTag(el))
      defaults.clear()
      defaults.add(choiceId)
      this.#activateTag(tagEl)
    }

    // 새로 추가된 선택지(temp)의 is_default도 동기화
    const pending = this.choiceCreates.find(c => c.tempId === choiceId && c.group_id === groupId)
    if (pending) pending.is_default = defaults.has(choiceId)
  }

  #activateTag(tagEl) {
    if (!tagEl) return
    tagEl.dataset.isDefault = "true"
    tagEl.classList.remove("border-default-slate/25", "bg-white", "text-default-slate")
    tagEl.classList.add("bg-primary", "border-primary", "text-white")
    const icon = tagEl.querySelector("[data-default-icon]")
    if (icon) {
      icon.classList.remove("hidden")
      icon.classList.add("inline")
    }
  }

  #deactivateTag(tagEl) {
    if (!tagEl) return
    tagEl.dataset.isDefault = "false"
    tagEl.classList.remove("bg-primary", "border-primary", "text-white")
    tagEl.classList.add("border-default-slate/25", "bg-white", "text-default-slate")
    const icon = tagEl.querySelector("[data-default-icon]")
    if (icon) {
      icon.classList.remove("inline")
      icon.classList.add("hidden")
    }
  }

  // ── 순서 이동 ──────────────────────────────────────────────

  moveUp(event) {
    const row = document.getElementById(`group-row-${event.currentTarget.dataset.groupId}`)
    const prev = row?.previousElementSibling
    if (prev) { row.parentNode.insertBefore(row, prev); this.orderChanged = true }
  }

  moveDown(event) {
    const row = document.getElementById(`group-row-${event.currentTarget.dataset.groupId}`)
    const next = row?.nextElementSibling
    if (next) { row.parentNode.insertBefore(next, row); this.orderChanged = true }
  }

  // ── 금액 입력 포맷 ─────────────────────────────────────────

  formatPriceInput(event) {
    const input = event.target
    const raw = input.value.replace(/[^0-9]/g, '')
    const num = Math.min(parseInt(raw) || 0, 99999999)
    input.value = num === 0 ? '0' : num.toLocaleString('ko-KR')
  }

  // ── 완료: 일괄 저장 ────────────────────────────────────────

  async save() {
    if (!confirm("저장하시겠습니까?")) return
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    const groupOrders = [...document.querySelectorAll('#option_group_list > [id^="group-row-"]')]
      .map((el, idx) => ({ id: el.id.replace('group-row-', ''), position: idx }))

    const groupDefaults = [...this.groupDefaultsChanged.entries()].map(([groupId, choiceIds]) => ({
      group_id:   groupId,
      choice_ids: [...choiceIds]
    }))

    const body = {
      brand_id:        this.brandIdValue,
      group_creates:   this.groupCreates.map(({ tempId, name, selection_type }) => ({ temp_id: tempId, name, selection_type })),
      group_destroys:  [...this.groupDestroys],
      choice_creates:  this.choiceCreates.map(({ tempId, group_id, name, price_delta, is_default }) => ({ temp_id: tempId, group_id, name, price_delta, is_default })),
      choice_destroys: this.choiceDestroys,
      group_defaults:  groupDefaults,
      group_orders:    groupOrders
    }

    try {
      const response = await fetch("/option_groups/batch_save", {
        method:  "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf,
          "Accept":       "text/vnd.turbo-stream.html"
        },
        body: JSON.stringify(body)
      })
      if (response.ok) {
        const html = await response.text()
        window.Turbo.renderStreamMessage(html)
        window.dispatchEvent(new CustomEvent("modals:close"))
      }
    } catch (e) {
      console.error("Option groups batch save failed:", e)
    }
  }

  // ── 닫기: 미저장 시 confirm ────────────────────────────────

  cancel() {
    if (this.isDirty && !confirm("저장하지 않으면 변경사항이 삭제됩니다.\n닫으시겠습니까?")) return
    window.dispatchEvent(new CustomEvent("modals:close"))
  }

  // ── DOM 빌더 ───────────────────────────────────────────────

  buildGroupRow(id, name, selectionType) {
    const isMulti  = selectionType === "multi"
    return `
      <div class="border border-table-border rounded-xl p-4" id="group-row-${id}" data-selection-type="${selectionType}">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <span class="font-bold text-default-slate">${name}</span>
            <span class="px-2.5 py-0.5 rounded-full bg-default-slate/10 text-default-slate">
              ${isMulti ? "다중 선택" : "단일 선택"}
            </span>
          </div>
          <div class="flex items-center gap-1 flex-shrink-0">
            <button type="button"
                    data-action="option-groups-manager#moveUp"
                    data-group-id="${id}"
                    class="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-full text-gray-500 hover:bg-gray-50">
              <i class="fa-solid fa-chevron-up text-sm"></i>
            </button>
            <button type="button"
                    data-action="option-groups-manager#moveDown"
                    data-group-id="${id}"
                    class="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-full text-gray-500 hover:bg-gray-50">
              <i class="fa-solid fa-chevron-down text-sm"></i>
            </button>
            <button type="button"
                    data-action="option-groups-manager#deleteGroup"
                    data-group-id="${id}" data-group-name="${name}"
                    class="w-8 h-8 flex items-center justify-center border border-danger/50 rounded-full text-danger hover:bg-danger/5">
              <i class="fa-solid fa-trash text-sm"></i>
            </button>
          </div>
        </div>
        <div class="flex flex-wrap gap-2 mt-3" id="choice-tags-${id}">
          <span data-no-choices class="text-default-slate/40">선택지 없음</span>
        </div>
        <form data-action="submit->option-groups-manager#addChoice" data-group-id="${id}"
              class="flex items-center gap-2 mt-3">
          <input type="text" name="name" placeholder="선택지 이름" required
                 class="flex-1 min-w-0 border border-table-border rounded-lg px-3 py-2">
          <input type="text" name="price_delta" value="0" placeholder="추가 금액"
                 inputmode="numeric" maxlength="10"
                 data-action="input->option-groups-manager#formatPriceInput"
                 class="w-36 border border-table-border rounded-lg px-3 py-2 text-right">
          <button type="submit" class="modal-pos-btn-small modal-pos-btn-border whitespace-nowrap">
            추가
          </button>
        </form>
      </div>`
  }

  buildChoiceTag(groupId, choiceId, name, priceDelta, isDefault) {
    const priceHtml = priceDelta !== 0
      ? `<span class="text-xs opacity-60">+${priceDelta.toLocaleString("ko-KR")}원</span>`
      : ""
    const tagCls  = isDefault
      ? "bg-primary border-primary text-white"
      : "border-default-slate/25 bg-white text-default-slate"
    const iconCls = isDefault ? "inline" : "hidden"
    return `
      <div class="flex items-center gap-1.5 px-3 py-1.5 rounded-full border cursor-pointer select-none font-medium transition-colors ${tagCls}"
           id="choice-tag-${groupId}-${choiceId}"
           data-is-default="${isDefault}"
           data-action="click->option-groups-manager#setDefaultChoice"
           data-group-id="${groupId}"
           data-choice-id="${choiceId}">
        <i data-default-icon class="fa-solid fa-check text-xs flex-shrink-0 ${iconCls}"></i>
        <span>${name}</span>
        ${priceHtml}
        <button type="button"
                data-action="click->option-groups-manager#deleteChoice"
                data-group-id="${groupId}" data-choice-id="${choiceId}"
                class="w-5 h-5 flex items-center justify-center rounded-full opacity-40 hover:opacity-100 hover:text-danger hover:bg-danger/10 flex-shrink-0 ml-1">
          <i class="fa-solid fa-xmark text-sm"></i>
        </button>
      </div>`
  }
}
